import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  type DialogProps,
  DialogTitle,
} from "@headlessui/react";
import { X } from "@untitled-ui/icons-react";
import classNames from "classnames";
import { type ElementType, type ReactNode } from "react";

import Button from "../forms/Button";
import Description from "../forms/Description";
import FeatureIcon from "../helpers/FeatureIcon";

type ModalProps<T extends ElementType = "div"> = {
  icon?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  children?: React.ReactNode;
  size?: keyof typeof sizes;
  footer?: ReactNode;
  showCloseButton?: boolean;
} & Omit<DialogProps<T>, "title">;

const sizes = {
  xs: "max-w-xs",
  sm: "max-w-sm",
  md: "max-w-[480px]",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
};

export default function Modal<TTag extends ElementType = "label">({
  title,
  description,
  size = "md",
  children,
  footer,
  icon,
  showCloseButton = true,
  ...props
}: ModalProps<TTag>) {
  return (
    <Dialog className="relative z-50" {...props}>
      <DialogBackdrop className="fixed inset-0 bg-gray-700/70 backdrop-blur-sm" />
      <div className="fixed inset-0 flex w-screen items-center justify-center p-4 sm:p-6">
        <DialogPanel
          className={classNames(
            "relative flex flex-col rounded-xl bg-white shadow-xl",
            sizes[size],
            "max-h-[80vh] w-full",
          )}
        >
          {showCloseButton && (
            <Button
              onClick={() => props.onClose?.(false)}
              color="link-gray"
              size="lg"
              square
              className="absolute end-4 top-3 p-2.5"
            >
              <X className="size-6 text-gray-500" />
            </Button>
          )}
          {(title || icon) && (
            <div className="flex items-start gap-4 border-b px-6 pt-6 pb-5">
              {icon && <FeatureIcon variant="modern">{icon}</FeatureIcon>}
              {(title || description) && (
                <div>
                  {title && (
                    <DialogTitle className="flex items-center gap-4 text-lg font-semibold">
                      <span>{title}</span>
                    </DialogTitle>
                  )}
                  {description && <Description>{description}</Description>}
                </div>
              )}
            </div>
          )}
          <div className="grow overflow-y-auto px-6 py-5">{children}</div>
          {footer}
        </DialogPanel>
      </div>
    </Dialog>
  );
}
